import { CommonModule } from "@angular/common";
import { AfterViewInit, Component, EnvironmentInjector, inject, OnInit } from "@angular/core";
import { IonicModule } from "@ionic/angular";
import { Style } from "@capacitor/status-bar";
import { LoadingService } from "./services/loading.service";
import { NavigationEnd, Router } from "@angular/router";
import { SharedService } from "./services/shared.service";
import { SystemService } from "./services/system.service";
import { SalesforceMarketingCloudService } from "./services/salesforce-marketing-cloud.service";
import { SalesforceUserSyncService } from "./services/salesforce-user-sync.service";
import { SalesforceEventsService } from "./core/salesforce";
import { sfLog } from "./core/salesforce/salesforce-debug.logger";
import { MessagingService } from "./core/messaging/messaging.service";
import { AnalyticsService } from "./core/analytics";
import { DeepLinkService } from "./core/deeplink";
import { filter } from "rxjs/operators";
import { withTimeout } from "./core/compatibility/with-timeout";
import { CameraRecoveryService } from "./core/compatibility/camera-recovery.service";

@Component({
  selector: "app-root",
  templateUrl: "app.component.html",
  styleUrls: ["app.component.scss"],
  standalone: true,
  imports: [IonicModule, CommonModule],
})
export class AppComponent implements OnInit, AfterViewInit {
  public environmentInjector = inject(EnvironmentInjector);

  loading = true;
  private loadingFailsafeTimer?: ReturnType<typeof setTimeout>;
  private readonly vinhetaPublicRoutes = new Set([
    "/login",
    "/intro",
    "/walkthrough",
    "/register",
    "/recovery-password",
    "/maintenance",
    "/blocked-outdated",
  ]);

  constructor(
    public shared: SharedService,
    public router: Router,
    private loadingService: LoadingService,
    public system: SystemService,
    private salesforceService: SalesforceMarketingCloudService,
    private salesforceUserSync: SalesforceUserSyncService,
    private sfEvents: SalesforceEventsService,
    private analytics: AnalyticsService,
    private deepLink: DeepLinkService,
    private cameraRecovery: CameraRecoveryService,
    private messaging: MessagingService
  ) {
    this.loadingService.loading$.subscribe((res) => {
      this.loading = res;
      void this.syncStatusBar(res);
      this.handleLoadingFailsafe(res);
    });
  }

  ngOnInit(): void {
    void this.cameraRecovery.initialize().catch(error => console.warn("[Camera] Recuperação indisponível", error));
    this.initAnalytics();
    this.initVinhetaRouteRelease();
    void this.initializeApp();
  }

  ngAfterViewInit(): void {
    void this.hideNativeSplash();
  }

  private async initializeApp(): Promise<void> {
    try {
      await withTimeout(this.shared.platform.ready(), 10000, "Plataforma");
    } catch (error) {
      console.error("[Startup] Falha em platform.ready():", error);
    }

    // Deep link: registrar a escuta o quanto antes para não perder eventos de
    // cold-start nem links recebidos com o app em background.
    try {
      await withTimeout(this.deepLink.initialize(), 10000, "Links de abertura");
    } catch (error) {
      console.error("[Startup] Falha em deepLink.initialize():", error);
    }

    try {
      this.verifyDeviceContext();
    } catch (error) {
      console.error("[Startup] Falha em verifyDeviceContext():", error);
    }

    try {
      this.initBackButton();
    } catch (error) {
      console.error("[Startup] Falha em initBackButton():", error);
    }

    try {
      this.verifyiOSContext();
    } catch (error) {
      console.error("[Startup] Falha em verifyiOSContext():", error);
    }

    // Register push listeners before initializing the marketing SDK.
    this.messaging.opened$.subscribe(action => {
      const url = action.message.data["url"];
      if (action.actionId !== "dismiss" && typeof url === "string") this.deepLink.openNotificationUrl(url);
    });
    setTimeout(() => {
      void (async () => {
        await withTimeout(this.initMessaging(), 15000, "FCM").catch(error => console.warn("[Startup] FCM indisponível", error));
        await withTimeout(this.initSalesforce(), 30000, "Salesforce").catch(error => console.warn("[Startup] Salesforce indisponível", error));
      })();
    }, 2500);
  }

  private initAnalytics(): void {
    void this.analytics.init().catch(error => console.warn("[Startup] Analytics indisponível", error));
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((e) => {
      const url = e.urlAfterRedirects;
      this.analytics.trackRoute(url);
      this.sfEvents.trackRoutePageView(url);
    });
  }

  private initVinhetaRouteRelease(): void {
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((e) => {
      const url = e.urlAfterRedirects.split("?")[0];
      if (this.vinhetaPublicRoutes.has(url)) {
        this.loadingService.hideVinheta();
        return;
      }
      // Qualquer rota fora de /tabs/home libera a vinheta (ex.: redirect do AuthGuard).
      if (url !== "/" && !url.startsWith("/tabs/home")) {
        this.loadingService.hideVinheta();
      }
    });
  }

  private async syncStatusBar(loading: boolean): Promise<void> {
    if (!this.shared.platform.is("capacitor") || !this.shared.platform.is("ios")) {
      return;
    }
    try {
      const { StatusBar } = await import("@capacitor/status-bar");
      await StatusBar.setStyle({ style: loading ? Style.Dark : Style.Light });
      await StatusBar.setBackgroundColor({ color: loading ? "#f40000" : "#efefef" });
    } catch (error) {
      console.warn("[StatusBar] Não foi possível atualizar status bar:", error);
    }
  }

  private async hideNativeSplash(): Promise<void> {
    if (!this.shared.platform.is("capacitor") && !this.shared.platform.is("cordova")) {
      return;
    }
    try {
      const { SplashScreen } = await import("@capacitor/splash-screen");
      await SplashScreen.hide({ fadeOutDuration: 0 });
    } catch (error) {
      console.warn("[Splash] Não foi possível ocultar splash nativo:", error);
    }
  }

  private handleLoadingFailsafe(active: boolean): void {
    if (this.loadingFailsafeTimer) {
      clearTimeout(this.loadingFailsafeTimer);
      this.loadingFailsafeTimer = undefined;
    }
    if (!active) {
      return;
    }
    this.loadingFailsafeTimer = setTimeout(() => {
      console.warn("[Loading] Failsafe acionado: encerrando vinheta global.");
      this.loading = false;
      this.loadingService.hideVinheta(true);
      const url = this.router.url.split("?")[0];
      if (url === "/" || url.startsWith("/tabs/home") || url === "/intro") {
        void this.shared.storage
          .get("introViewed")
          .then((res) => this.router.navigateByUrl(res ? "/login" : "/intro"))
          .catch(() => this.router.navigateByUrl("/intro"));
      }
    }, 15000);
  }

  verifyiOSContext() {
    if (this.shared.platform.is("capacitor") && this.shared.platform.is("ios")) {
      this.shared.ios = true;
    } else {
      this.shared.ios = false;
    }
  }

  verifyDeviceContext() {
    const breakpoint = 768;
    this.shared.web = window.innerWidth >= breakpoint;
    window.addEventListener("resize", () => {
      this.shared.web = window.innerWidth >= breakpoint;
    });
  }

  async initMessaging() {
    try { await this.messaging.initialize(); }
    catch { console.warn("[Push] Inicialização pendente; tente novamente ao retomar o app"); }
  }

  async initSalesforce() {
    try {
      const initialized = await this.salesforceService.initialize();

      if (initialized) {
        this.sfEvents.markReady();
        await this.salesforceUserSync.syncCurrentUserIfAuthenticated();
        const state = await this.salesforceService.getSDKState();
        sfLog.ok("Integração Salesforce concluída no startup", {
          modo: this.salesforceService.isWebTransport() ? "web" : "nativo",
          initialized: state.initialized,
          contactKey: state.contactKey ?? "(não definido)",
          pushEnabled: state.pushEnabled,
          deviceId: state.deviceId ?? "(indisponível)",
        });
        return;
      }

      sfLog.error(
        "Integração Salesforce não inicializada — eventos sf_* não serão enviados até o SDK ficar pronto"
      );
    } catch (error) {
      sfLog.error("Erro inesperado ao inicializar Salesforce no startup", error);
    }
  }

  initBackButton() {
    this.shared.platform.backButton.subscribeWithPriority(10, async () => {
      const modal = await this.shared.modalController.getTop();
      if (modal) {
        return;
      }
      if (
        this.router.url !== "/tabs/home" &&
        this.router.url !== "/tabs/cart" &&
        this.router.url !== "/tabs/catalog" &&
        this.router.url !== "/tabs/menu" &&
        this.router.url !== "/register" &&
        this.router.url !== "/intro" &&
        this.router.url !== "/login"
      ) {
        this.shared.navCtrl.back();
      }
    });
  }
}
